/**
 * DMS 90-Day Strategy Operator — Sprint 3 Calendar page.
 *
 * Three tabs: Today · This Week · 90-Day.
 * Rolling calendar health chip in the header (healthy | attention | action | critical).
 *
 * Empty state (no calendar yet): shows one big "Generate 90-day calendar" CTA
 * that kicks off the async job and polls for completion.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { meta } from "@/lib/metaPixel";
import {
  ArrowLeft, Loader2, Sparkles, CheckCircle2, CircleAlert, CircleX,
  Activity, Calendar as CalendarIcon, Compass, ChevronLeft, ChevronRight,
  SkipForward, RotateCw,
} from "lucide-react";

const HEALTH = {
  healthy: {
    label: "Healthy", tone: "bg-emerald-50 text-emerald-800 border-emerald-200",
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    tip: "75–90 planned days ahead. Nothing to do — keep executing.",
  },
  attention: {
    label: "Attention soon", tone: "bg-amber-50 text-amber-800 border-amber-200",
    icon: <Activity className="w-3.5 h-3.5" />,
    tip: "60–74 planned days. Refresh your calendar in the next 2 weeks.",
  },
  action: {
    label: "Action needed", tone: "bg-orange-50 text-orange-800 border-orange-200",
    icon: <CircleAlert className="w-3.5 h-3.5" />,
    tip: "Under 60 planned days. Generate a new cycle now.",
  },
  critical: {
    label: "Critical", tone: "bg-red-50 text-red-800 border-red-200",
    icon: <CircleX className="w-3.5 h-3.5" />,
    tip: "Under 30 planned days. You will run out — regenerate immediately.",
  },
};

// Rotating palette used to colour-code pillars on the 90-day grid.
const PILLAR_COLORS = [
  "bg-[#0A2342]/90 text-white",
  "bg-[#4FD1C5] text-[#0A2342]",
  "bg-[#1D4E89] text-white",
  "bg-[#F97316] text-white",
  "bg-[#8B5CF6] text-white",
  "bg-[#059669] text-white",
  "bg-[#DB2777] text-white",
];


function pollJob(jobId, { timeoutMs = 240_000, intervalMs = 2500 } = {}) {
  const started = Date.now();
  return new Promise(async (resolve, reject) => {
    while (Date.now() - started < timeoutMs) {
      await new Promise(r => setTimeout(r, intervalMs));
      try {
        const { data: job } = await api.get(`/strategy/jobs/${jobId}`);
        if (job.status === "done") return resolve(job);
        if (job.status === "failed") return reject(new Error(job.error || "Job failed"));
      } catch (err) {
        const s = err?.response?.status;
        if (s === 401 || s === 403) return reject(err);
        if (s && s !== 404) continue;
        return reject(err);
      }
    }
    reject(new Error("Calendar job timed out. Reload — it may still finish in the background."));
  });
}


export default function StrategyCalendar() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [health, setHealth] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [busy, setBusy] = useState(null); // "generate" | "task:{id}"
  const [tab, setTab] = useState("today");
  const [weekNum, setWeekNum] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/strategy/calendar");
      setMeta(data.meta || null);
      setTasks(data.tasks || []);
      setHealth(data.health || null);
      setAccessDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(err?.response?.data?.detail || "Could not load calendar.");
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Pillar colour map by id (assigned deterministically in order encountered).
  const pillarColorMap = useMemo(() => {
    const seen = [];
    for (const t of tasks) {
      if (t.pillar_id && !seen.includes(t.pillar_id)) seen.push(t.pillar_id);
    }
    return Object.fromEntries(seen.map((id, i) => [id, PILLAR_COLORS[i % PILLAR_COLORS.length]]));
  }, [tasks]);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const todayTask = useMemo(() => tasks.find(t => t.date === todayIso) || null, [tasks, todayIso]);
  const weekTasks = useMemo(() => tasks.filter(t => t.week_number === weekNum), [tasks, weekNum]);

  const handleGenerate = async () => {
    setBusy("generate");
    try {
      const { data } = await api.post("/strategy/calendar/generate", {});
      await pollJob(data.job_id);
      toast.success(`Calendar generated (−${data.credits_spent} credits).`);
      await load();
      // Meta: DMS_CalendarGenerated is a critical activation event. Fire only
      // AFTER the async job completes and we've reloaded the calendar so we
      // know it actually landed. Dedupes per-user across sessions.
      if (user?.user_id) {
        // Read tasks off the just-refreshed state via a functional setter so
        // we don't race the effect that populates `tasks`.
        const currentTasks = (await api.get("/strategy/calendar")).data?.tasks || [];
        const channelsSet = new Set(currentTasks.map(t => t.channel).filter(Boolean));
        meta.calendarGenerated(user.user_id, {
          calendar_days: currentTasks.length,
          channels_count: channelsSet.size,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Calendar generation failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleTaskStatus = async (taskId, status) => {
    setBusy(`task:${taskId}`);
    // Optimistic update
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, status } : t)));
    try {
      await api.patch(`/strategy/calendar/task/${taskId}`, { status });
      toast.success(`Marked ${status}.`);
      // Refresh health so the chip updates immediately.
      const { data } = await api.get("/strategy/calendar/health");
      setHealth(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update task.");
      await load();
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-2 text-[#64748B]" data-testid="strategy-calendar-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your calendar…
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="max-w-3xl px-5 sm:px-6 lg:px-10 py-10" data-testid="calendar-access-denied">
        <Link to="/strategy" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Strategy home
        </Link>
        <div className="dms-card p-6">
          <div className="dms-label mb-1">Not available on this plan</div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-[#0A2342]">
            The 90-day calendar is a Business Unlimited feature.
          </h1>
          <p className="mt-2 text-[#475569] text-[14px]">
            Upgrade to unlock the Strategy Operator and the rolling calendar. Your Dive Brain stays exactly where it is.
          </p>
          <Link
            to="/billing"
            data-testid="calendar-upgrade-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#152F5A]"
          >
            View plans <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const hasCalendar = tasks.length > 0;

  return (
    <div className="max-w-6xl px-5 sm:px-6 lg:px-10 py-6" data-testid="strategy-calendar">
      <Link to="/strategy" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Strategy home
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="dms-label mb-1">90-day rolling calendar</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Your marketing calendar</h1>
          {meta?.start_date && (
            <div className="text-[13px] text-[#64748B] mt-1">
              Cycle started <b>{meta.start_date}</b> · generated {new Date(meta.generated_at).toLocaleDateString()}
            </div>
          )}
        </div>
        {hasCalendar && health && (
          <HealthChip health={health} />
        )}
      </div>

      {!hasCalendar ? (
        <EmptyState busy={busy} onGenerate={handleGenerate} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1.5 border-b border-[#E2E8F0] mb-4" role="tablist">
            <TabBtn active={tab === "today"} onClick={() => setTab("today")} testId="tab-today">Today</TabBtn>
            <TabBtn active={tab === "week"} onClick={() => setTab("week")} testId="tab-week">This Week</TabBtn>
            <TabBtn active={tab === "grid"} onClick={() => setTab("grid")} testId="tab-grid">90-Day</TabBtn>
            <button
              onClick={handleGenerate}
              disabled={!!busy}
              data-testid="regenerate-calendar-btn"
              className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#64748B] hover:text-[#0A2342] px-2 py-1 disabled:opacity-50"
              title="Regenerate the full 90-day calendar (5 credits)"
            >
              {busy === "generate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
              Regenerate calendar (5)
            </button>
          </div>

          {tab === "today" && (
            <TodayView task={todayTask} busy={busy} onStatus={handleTaskStatus} health={health} />
          )}
          {tab === "week" && (
            <WeekView
              weekNum={weekNum}
              setWeekNum={setWeekNum}
              tasks={weekTasks}
              summaries={meta?.weekly_summaries || []}
              pillarColorMap={pillarColorMap}
              onStatus={handleTaskStatus}
              busy={busy}
            />
          )}
          {tab === "grid" && (
            <GridView tasks={tasks} meta={meta} pillarColorMap={pillarColorMap} setTab={setTab} setWeekNum={setWeekNum} />
          )}
        </>
      )}
    </div>
  );
}


/* ---------------- Sub-components ---------------- */

function HealthChip({ health }) {
  const cfg = HEALTH[health.status] || HEALTH.action;
  return (
    <div
      data-testid={`calendar-health-${health.status}`}
      className={`inline-flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 ${cfg.tone}`}
      title={cfg.tip}
    >
      <div className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.1em]">
        {cfg.icon} {cfg.label}
      </div>
      <div className="text-[13px] font-semibold">{health.days_ahead} planned days ahead</div>
    </div>
  );
}


function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={
        active
          ? "px-3 py-2 text-[13px] font-semibold text-[#0A2342] border-b-2 border-[#0A2342] -mb-px"
          : "px-3 py-2 text-[13px] font-medium text-[#64748B] hover:text-[#0A2342]"
      }
    >
      {children}
    </button>
  );
}


function EmptyState({ busy, onGenerate }) {
  return (
    <div className="dms-card p-6 border-2 border-[#4FD1C5]/40 flex flex-col sm:flex-row items-start gap-4" data-testid="calendar-empty">
      <div className="w-12 h-12 rounded-md bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center flex-shrink-0">
        <CalendarIcon className="w-6 h-6" />
      </div>
      <div className="flex-1">
        <div className="font-heading font-bold text-[#0A2342] text-[16px]">Generate your 90-day calendar</div>
        <div className="text-[13px] text-[#64748B] mt-1">
          DMS will map your approved pillars, buckets and campaign arcs across 90 days —
          respecting your posting frequency and channels. Each day gets one content task
          (or a rest day) with hook, angle and CTA. Costs <b>5 Strategy Credits</b>, takes ~60–120 s.
        </div>
        <button
          onClick={onGenerate}
          disabled={busy === "generate"}
          data-testid="generate-calendar-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
        >
          {busy === "generate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy === "generate" ? "Generating (up to 2 minutes)…" : "Generate 90-day calendar"}
        </button>
      </div>
    </div>
  );
}


function TodayView({ task, busy, onStatus, health }) {
  if (!task) {
    return (
      <div className="dms-card p-6" data-testid="today-empty">
        <div className="text-[15px] text-[#0A2342] font-semibold">No task for today</div>
        <div className="text-[13px] text-[#64748B] mt-1">
          Today isn&apos;t in your current 90-day cycle, or the cycle has finished. Generate a new one.
        </div>
      </div>
    );
  }
  if (task.status === "rest") {
    return (
      <div className="dms-card p-6 text-center" data-testid="today-rest">
        <div className="text-[15px] text-[#0A2342] font-semibold">Rest day 🌊</div>
        <div className="text-[13px] text-[#64748B] mt-1">
          Nothing to post today. Batch-record for tomorrow or take the day off.
        </div>
      </div>
    );
  }
  return (
    <div className="dms-card p-5 sm:p-6" data-testid="today-task">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <div className="text-[12px] uppercase tracking-[0.15em] font-bold text-[#64748B]">
          Day {task.day_number} · Week {task.week_number} · {task.day_of_week}
        </div>
        <StatusBadge status={task.status} />
      </div>
      <div className="text-[13px] text-[#1D4E89] font-semibold">
        {task.channel} · {task.format}
      </div>
      <h2 className="text-xl sm:text-2xl font-heading font-bold text-[#0A2342] mt-2 leading-tight">
        {task.hook}
      </h2>
      <div className="mt-3 text-[14px] text-[#334155] leading-relaxed"><b>Angle: </b>{task.angle}</div>
      <div className="mt-2 text-[14px] text-[#0A2342]"><b>CTA: </b>{task.cta}</div>
      {task.notes && (
        <div className="mt-2 text-[12.5px] text-[#64748B]"><b>Prep note: </b>{task.notes}</div>
      )}
      <div className="text-[11.5px] text-[#94A3B8] mt-3">
        Pillar: <b>{task.pillar_id}</b> · Bucket: <b>{task.bucket_id}</b>
      </div>
      {task.status !== "done" && task.status !== "skipped" && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            onClick={() => onStatus(task.id, "done")}
            disabled={busy === `task:${task.id}`}
            data-testid="task-done-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0A2342] text-white px-4 py-2 text-[13px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
          >
            {busy === `task:${task.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Mark done
          </button>
          <button
            onClick={() => onStatus(task.id, "skipped")}
            disabled={busy === `task:${task.id}`}
            data-testid="task-skip-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E8F0] px-4 py-2 text-[13px] font-semibold text-[#64748B] hover:border-[#1D4E89] hover:text-[#0A2342] disabled:opacity-60"
          >
            <SkipForward className="w-3.5 h-3.5" /> Skip
          </button>
          <Link
            to={`/tools/${task.format?.toLowerCase() === "carousel" ? "carousel" : task.format?.toLowerCase() === "email" ? "email-newsletter" : task.format?.toLowerCase() === "blog" ? "seo-blog" : "reel"}?task=${task.id}`}
            data-testid="task-generate-cta"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#0A2342] text-[#0A2342] px-4 py-2 text-[13px] font-semibold hover:bg-[#0A2342] hover:text-white"
          >
            <Compass className="w-3.5 h-3.5" /> Generate this {task.format}
          </Link>
        </div>
      )}
    </div>
  );
}


function WeekView({ weekNum, setWeekNum, tasks, summaries, pillarColorMap, onStatus, busy }) {
  const summary = summaries.find(w => Number(w.week) === Number(weekNum));
  return (
    <div data-testid="week-view">
      <div className="dms-card p-4 mb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setWeekNum(Math.max(1, weekNum - 1))}
            disabled={weekNum <= 1}
            data-testid="week-prev"
            className="inline-flex items-center gap-1 text-[13px] text-[#334155] disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <div className="text-center">
            <div className="dms-label">Week {weekNum} of 13</div>
            {summary && <div className="font-heading font-bold text-[#0A2342] text-[16px] mt-0.5">{summary.theme}</div>}
            {summary?.focus && <div className="text-[12.5px] text-[#64748B] mt-0.5">{summary.focus}</div>}
          </div>
          <button
            onClick={() => setWeekNum(Math.min(13, weekNum + 1))}
            disabled={weekNum >= 13}
            data-testid="week-next"
            className="inline-flex items-center gap-1 text-[13px] text-[#334155] disabled:opacity-30"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2" data-testid="week-tasks">
        {tasks.length === 0 && (
          <div className="text-[13px] text-[#94A3B8] text-center py-6">No tasks in this week.</div>
        )}
        {tasks.map((t) => (
          <WeekDayRow key={t.id} task={t} pillarColorMap={pillarColorMap} onStatus={onStatus} busy={busy} />
        ))}
      </div>
    </div>
  );
}


function WeekDayRow({ task, pillarColorMap, onStatus, busy }) {
  const pillarColor = pillarColorMap[task.pillar_id] || "bg-[#F1F5F9] text-[#334155]";
  if (task.status === "rest") {
    return (
      <div className="rounded-md border border-dashed border-[#E2E8F0] p-3 flex items-center gap-3 opacity-70">
        <div className="w-12 text-center">
          <div className="text-[10.5px] uppercase tracking-[0.15em] text-[#94A3B8]">{task.day_of_week}</div>
          <div className="text-[15px] font-bold text-[#94A3B8]">D{task.day_number}</div>
        </div>
        <div className="flex-1 text-[13px] text-[#64748B]">Rest day</div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-[#E2E8F0] p-3 flex flex-wrap items-start gap-3" data-testid={`week-row-${task.day_number}`}>
      <div className="w-12 text-center flex-shrink-0">
        <div className="text-[10.5px] uppercase tracking-[0.15em] text-[#94A3B8]">{task.day_of_week}</div>
        <div className="text-[15px] font-bold text-[#0A2342]">D{task.day_number}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          <span className={`text-[10.5px] font-semibold uppercase tracking-[0.05em] rounded px-1.5 py-0.5 ${pillarColor}`}>
            {task.pillar_id}
          </span>
          <span className="text-[11px] text-[#64748B]">{task.channel} · {task.format}</span>
          <span className="ml-auto"><StatusBadge status={task.status} /></span>
        </div>
        <div className="text-[13.5px] text-[#0A2342] font-semibold leading-snug">{task.hook}</div>
        <div className="text-[12px] text-[#475569] mt-0.5">{task.angle}</div>
        <div className="text-[12px] text-[#334155] mt-0.5"><b>CTA:</b> {task.cta}</div>
      </div>
      {task.status !== "done" && task.status !== "skipped" && (
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={() => onStatus(task.id, "done")}
            disabled={busy === `task:${task.id}`}
            data-testid={`row-done-${task.day_number}`}
            className="text-[11.5px] font-semibold text-[#0A2342] hover:underline"
          >Done</button>
          <button
            onClick={() => onStatus(task.id, "skipped")}
            disabled={busy === `task:${task.id}`}
            data-testid={`row-skip-${task.day_number}`}
            className="text-[11.5px] text-[#94A3B8] hover:text-[#0A2342] hover:underline"
          >Skip</button>
        </div>
      )}
    </div>
  );
}


function StatusBadge({ status }) {
  if (status === "done") return <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.1em] rounded bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5"><CheckCircle2 className="w-3 h-3" /> Done</span>;
  if (status === "skipped") return <span className="inline-flex items-center text-[10.5px] font-bold uppercase tracking-[0.1em] rounded bg-slate-100 text-slate-500 border border-slate-200 px-1.5 py-0.5">Skipped</span>;
  if (status === "rest") return <span className="inline-flex items-center text-[10.5px] font-bold uppercase tracking-[0.1em] rounded bg-blue-50 text-blue-800 border border-blue-200 px-1.5 py-0.5">Rest</span>;
  return <span className="inline-flex items-center text-[10.5px] font-bold uppercase tracking-[0.1em] rounded bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0] px-1.5 py-0.5">Planned</span>;
}


function GridView({ tasks, meta, pillarColorMap, setTab, setWeekNum }) {
  // Group into 3 months × ~30 days each.
  const monthly = meta?.monthly_themes || [];
  const months = [1, 2, 3].map(m => ({
    number: m,
    theme: monthly.find(x => Number(x.month) === m),
    days: tasks.filter(t => t.month_number === m),
  }));
  const legend = Object.entries(pillarColorMap);

  return (
    <div data-testid="grid-view">
      {legend.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5 text-[11.5px]">
          <span className="text-[#64748B] mr-1">Pillars:</span>
          {legend.map(([id, cls]) => (
            <span key={id} className={`rounded px-1.5 py-0.5 font-semibold ${cls}`}>{id}</span>
          ))}
        </div>
      )}
      <div className="space-y-6">
        {months.map(m => (
          <div key={m.number} data-testid={`grid-month-${m.number}`}>
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <div className="dms-label">Month {m.number}</div>
                {m.theme?.theme && <div className="font-heading font-bold text-[#0A2342] text-[15.5px]">{m.theme.theme}</div>}
                {m.theme?.narrative && <div className="text-[12.5px] text-[#64748B]">{m.theme.narrative}</div>}
              </div>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-7 gap-1.5">
              {m.days.map(day => (
                <button
                  key={day.id}
                  onClick={() => { setWeekNum(day.week_number); setTab("week"); }}
                  data-testid={`day-cell-${day.day_number}`}
                  title={`${day.date} · ${day.status === "rest" ? "Rest" : (day.hook || "")}`}
                  className={`h-16 rounded-md border border-[#E2E8F0] p-1.5 text-left overflow-hidden hover:border-[#0A2342] transition-colors ${
                    day.status === "done" ? "opacity-60 line-through" : ""
                  }`}
                >
                  <div className={`text-[9px] font-bold uppercase tracking-[0.1em] rounded inline-block px-1 py-0.5 ${
                    day.pillar_id ? (pillarColorMap[day.pillar_id] || "bg-[#F1F5F9] text-[#475569]") : "bg-[#F1F5F9] text-[#94A3B8]"
                  }`}>
                    D{day.day_number}
                  </div>
                  <div className="text-[9.5px] text-[#64748B] mt-0.5 truncate">{day.day_of_week}</div>
                  <div className="text-[10px] text-[#334155] leading-tight line-clamp-2 mt-0.5">
                    {day.status === "rest" ? "Rest" : day.channel}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
